import background from "@assets/bg_player.jpg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import {
	AppBar,
	Box,
	Button,
	IconButton,
	Menu,
	MenuItem,
	Stack,
	Toolbar,
	Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ProfileDialog from "@/frontend/components/ProfileDialog";
import {
	createFrontendAuthClient,
	createFrontendTrpcClient,
} from "@/frontend/utils";

// Small tRPC client with cookies enabled
const trpcClient = createFrontendTrpcClient();

// Better Auth client
const authClient = createFrontendAuthClient();

interface HockeyPlannerRootProps {
	title: string;
	children: React.ReactNode;
	showBackButton?: boolean;
}

const HockeyPlannerRoot: React.FC<HockeyPlannerRootProps> = ({
	title,
	children,
	showBackButton = false,
}) => {
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const [profileOpen, setProfileOpen] = useState(false);

	// Get user data
	const { data: meData, error: meError } = useQuery({
		queryKey: ["me"],
		queryFn: async () => trpcClient.user.getOwn.query(),
		retry: false,
	});
	const loggedIn = !!meData && !meError;
	const displayName = meData?.user?.displayname || meData?.user?.email;
	const isManager = meData?.user?.is_manager ?? false;

	return (
		<Box
			sx={{
				position: "relative",
				minHeight: "100dvh", // use dynamic viewport height to avoid iOS Safari gaps
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			{/* Fixed background layer for reliable mobile rendering (avoids iOS background-attachment bugs) */}
			<Box
				aria-hidden={true}
				sx={{
					position: "fixed",
					inset: 0,
					zIndex: 0,
					backgroundImage: `url(${background})`,
					backgroundRepeat: "no-repeat",
					backgroundPosition: "center center",
					backgroundSize: "cover",
					// Improve scrolling performance on iOS
					WebkitTransform: "translateZ(0)",
				}}
			/>
			<Box
				sx={{
					position: "relative",
					zIndex: 1,
					display: "flex",
					flexDirection: "column",
					minHeight: "inherit",
				}}
			>
				<AppBar position="sticky">
					<Toolbar>
						{showBackButton && (
							<IconButton
								edge="start"
								color="inherit"
								aria-label="zurück"
								onClick={() => navigate(-1)}
								sx={{ mr: 2 }}
							>
								<ArrowBackIcon />
							</IconButton>
						)}
						<Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
							{title}
						</Typography>
						{loggedIn ? (
							<Stack
								direction="row"
								alignItems="center"
								sx={{ mr: 2, cursor: "pointer" }}
								onClick={(e) => setMenuAnchor(e.currentTarget)}
								title="Menü öffnen"
								data-testid="user-menu-trigger"
							>
								<PersonIcon sx={{ mr: 1 }} />
								<Typography variant="body1">{displayName}</Typography>
								<ExpandMoreIcon />
							</Stack>
						) : (
							<>
								<Typography variant="caption" sx={{ mr: 1 }}>
									Schon eingetragen?
								</Typography>
								<Button color="inherit" onClick={() => navigate("/")}>
									Login
								</Button>
							</>
						)}
					</Toolbar>
				</AppBar>
				<Menu
					anchorEl={menuAnchor}
					open={!!menuAnchor}
					onClose={() => setMenuAnchor(null)}
				>
					<MenuItem
						onClick={() => {
							// Open profile dialog (handled by this root component so it works from any view)
							setProfileOpen(true);
							setMenuAnchor(null);
						}}
					>
						Profil bearbeiten
					</MenuItem>
					{isManager && (<>
						<MenuItem
							onClick={() => {
								navigate("/payments-import");
								setMenuAnchor(null);
							}}
						>
							Zahlungen importieren
						</MenuItem>
						<MenuItem
							onClick={() => {
								navigate("/outstanding-payments");
								setMenuAnchor(null);
							}}
						>
							Ausstehende Zahlungen
						</MenuItem>
						</>
					)}
					<MenuItem
						onClick={async () => {
							try {
								await authClient.signOut();
								qc.invalidateQueries({ queryKey: ["me"] });
								qc.invalidateQueries({ queryKey: ["events"] });
								navigate("/");
							} catch (e: unknown) {
								const errorMessage =
									e instanceof Error ? e.message : "Logout fehlgeschlagen";
								console.error(errorMessage);
							}
							setMenuAnchor(null);
						}}
						data-testid="header-logout"
					>
						Logout
					</MenuItem>
				</Menu>
				{children}
				<ProfileDialog
					open={profileOpen}
					onClose={() => setProfileOpen(false)}
					title="Profil bearbeiten"
					user={meData?.user}
					onSubmit={async (data) => {
						await trpcClient.user.updateProfile.mutate({
							displayname: data.displayname,
							firstname: data.firstname,
							lastname: data.lastname,
							mobile: data.mobile,
						});
						await qc.invalidateQueries({ queryKey: ["me"] });
						setProfileOpen(false);
					}}
				/>
			</Box>
		</Box>
	);
};

export default HockeyPlannerRoot;
